import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  RefreshCw,
  Save,
  Trash2,
  UserRoundCheck,
  X,
} from "lucide-react";

import {
  CareerEvent,
  CareerEventKind,
  CareerEventPayload,
  CareerEventStatus,
  CareerEventVisibility,
  CareerUser,
  CohortCalendarEvent,
  careerApi,
} from "../api/career";
import { useAppSelector } from "../hooks/reducer";

const DATETIME_STEP_SECONDS = 5 * 60;

const kindLabels: Record<CareerEventKind, string> = {
  quick_work: "Миттєва робота",
  partner_vacancy: "Вакансія",
  consultation: "Кар'єрна консультація",
};

const statusLabels: Record<CareerEventStatus, string> = {
  scheduled: "Заплановано",
  active: "Активно",
  completed: "Завершено",
  cancelled: "Скасовано",
  closed: "Закрито",
};

const visibilityLabels: Record<CareerEventVisibility, string> = {
  draft: "Чернетка",
  published: "Опубліковано",
  archived: "Архів",
};

const toLocalInput = (value: Date | string) => {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const defaultTimes = () => {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 1);
  return {
    startsAt: toLocalInput(startsAt),
    endsAt: toLocalInput(endsAt),
  };
};

const emptyForm = (kind: CareerEventKind): CareerEventPayload => ({
  kind,
  title: "",
  company: "",
  description: "",
  requirements: "",
  responsibilities: "",
  benefits: "",
  hasReservation: false,
  isInstantWork: false,
  isDigitalWork: false,
  payout: "",
  startLabel: "",
  formatLabel: "",
  schedule: "",
  location: "",
  employerContact: "",
  tags: [],
  ...defaultTimes(),
  status: kind === "consultation" ? "scheduled" : "active",
  visibility: kind === "consultation" ? "draft" : "published",
  primaryParticipantId: "",
  participantIds: [],
});

const eventDateKey = (value: string | Date) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getParticipantName = (event: CareerEvent) => {
  const participant = event.primaryParticipantId;
  if (!participant || typeof participant === "string") return "";
  return participant.username || participant.telephone || participant.email || "";
};

const getEventPlaceText = (event: CareerEvent) =>
  event.kind === "quick_work" || event.isInstantWork
    ? event.formatLabel || event.location || "Формат не вказано"
    : event.location || "Локацію не вказано";

const splitFormList = (value?: string) =>
  String(value || "")
    .split(/\r?\n|;|•|▫️|✅/)
    .map((item) => item.trim().replace(/^[-–—]\s*/, ""))
    .filter(Boolean);

const getFormJobCategory = (form: CareerEventPayload) => {
  if (form.isDigitalWork) return "Цифрова робота";
  if (form.isInstantWork || form.kind === "quick_work") return "Миттєва робота";
  return "Усі пропозиції";
};

type CalendarEntry =
  | (CareerEvent & { calendarSource?: "career" })
  | CohortCalendarEvent;

const AdminCareerPage = () => {
  const currentUser = useAppSelector((state) => state.authReducer.data?.user);
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [events, setEvents] = useState<CareerEvent[]>([]);
  const [cohortEvents, setCohortEvents] = useState<CohortCalendarEvent[]>([]);
  const [users, setUsers] = useState<CareerUser[]>([]);
  const [includeCohortEvents, setIncludeCohortEvents] = useState(false);
  const [month, setMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeKind, setActiveKind] = useState<CareerEventKind>("consultation");
  const [formOpen, setFormOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [form, setForm] = useState<CareerEventPayload>(emptyForm("consultation"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [eventData, userData] = await Promise.all([
        careerApi.listEvents(isSuperAdmin && includeCohortEvents),
        careerApi.listUsers(),
      ]);
      setEvents(eventData.events);
      setCohortEvents(eventData.cohortEvents);
      setUsers(userData);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося завантажити кар'єрний календар."
      );
    } finally {
      setLoading(false);
    }
  }, [includeCohortEvents, isSuperAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allEntries: CalendarEntry[] = useMemo(
    () => [...events, ...(isSuperAdmin && includeCohortEvents ? cohortEvents : [])],
    [cohortEvents, events, includeCohortEvents, isSuperAdmin]
  );

  const entriesByDate = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    allEntries.forEach((event) => {
      const key = eventDateKey(event.startsAt);
      map.set(key, [...(map.get(key) || []), event]);
    });
    return map;
  }, [allEntries]);

  const selectedEntries = entriesByDate.get(eventDateKey(selectedDate)) || [];

  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayOffset = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const calendarCells = [
    ...Array.from({ length: mondayOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  const openCreate = (kind: CareerEventKind) => {
    setActiveKind(kind);
    setEditingEventId(null);
    setForm(emptyForm(kind));
    setFormOpen(true);
  };

  const openEdit = (event: CareerEvent) => {
    setActiveKind(event.kind);
    setEditingEventId(event._id);
    setForm({
      kind: event.kind,
      title: event.title || "",
      company: event.company || "",
      description: event.description || "",
      requirements: event.requirements || "",
      responsibilities: event.responsibilities || "",
      benefits: event.benefits || "",
      hasReservation: Boolean(event.hasReservation),
      isInstantWork: Boolean(event.isInstantWork || event.kind === "quick_work"),
      isDigitalWork: Boolean(event.isDigitalWork),
      payout: event.payout || "",
      startLabel: event.startLabel || "",
      formatLabel: event.formatLabel || "",
      schedule: event.schedule || "",
      location: event.location || "",
      employerContact: event.employerContact || "",
      tags: event.tags || [],
      startsAt: toLocalInput(event.startsAt),
      endsAt: toLocalInput(event.endsAt),
      status: event.status,
      visibility: event.visibility,
      primaryParticipantId:
        typeof event.primaryParticipantId === "string"
          ? event.primaryParticipantId
          : event.primaryParticipantId?._id || "",
      participantIds: (event.participantIds || [])
        .map((participant) =>
          typeof participant === "string" ? participant : participant._id
        )
        .filter(Boolean),
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingEventId(null);
  };

  const saveEvent = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        kind:
          form.kind === "consultation"
            ? ("consultation" as CareerEventKind)
            : ("partner_vacancy" as CareerEventKind),
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        participantIds: form.primaryParticipantId ? [form.primaryParticipantId] : [],
      };

      if (editingEventId) {
        await careerApi.updateEvent(editingEventId, payload);
        setMessage("Подію оновлено.");
      } else {
        await careerApi.createEvent(payload);
        setMessage("Подію створено.");
      }

      closeForm();
      await loadData();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Не вдалося зберегти подію.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id: string) => {
    if (!window.confirm("Видалити цю кар'єрну подію?")) return;
    try {
      await careerApi.deleteEvent(id);
      setMessage("Подію видалено.");
      await loadData();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Не вдалося видалити подію.");
    }
  };

  return (
    <section className="min-h-full bg-[#f4f7fb] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid gap-5 border-b border-black/10 pb-6 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a6f00]">
              Кар'єрний центр
            </p>
            <h1 className="mt-1 text-[34px] font-black uppercase leading-none tracking-[-0.04em] text-black sm:text-[46px]">
              Робота і консультації
            </h1>
            <p className="mt-3 max-w-[780px] text-sm font-semibold leading-6 text-slate-600">
              Тут кар'єрний менеджер створює швидкі роботи, вакансії партнерів
              і назначає персональні консультації. У календарі видно всі
              кар'єрні події по датах.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => openCreate("partner_vacancy")}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[3px_3px_0_#000]"
            >
              <BriefcaseBusiness size={17} />
              + Вакансія
            </button>
            <button
              type="button"
              onClick={() => openCreate("consultation")}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-black px-4 text-sm font-black text-[#ffd400] shadow-[3px_3px_0_#ffd400]"
            >
              <UserRoundCheck size={17} />
              + Консультація
            </button>
          </div>
        </div>

        {message && (
          <div className="mt-5 rounded-[16px] border border-amber-200 bg-[#fff8cf] px-4 py-3 text-sm font-bold text-black">
            {message}
          </div>
        )}

        <div className="mt-6 grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[26px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                  Календар подій
                </p>
                <h2 className="text-2xl font-black text-black">
                  {month.toLocaleString("uk-UA", { month: "long", year: "numeric" })}
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isSuperAdmin && (
                  <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-[#fffdf2] px-4 text-sm font-black text-black">
                    <input
                      type="checkbox"
                      checked={includeCohortEvents}
                      onChange={(event) => setIncludeCohortEvents(event.target.checked)}
                      className="h-4 w-4 accent-[#ffd400]"
                    />
                    Показати події потоків
                  </label>
                )}
                <button
                  type="button"
                  onClick={loadData}
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-black"
                >
                  <RefreshCw size={18} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
                  }
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-black"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
                  }
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-black"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 border-b border-slate-200 bg-[#fffdf2] text-center text-xs font-black uppercase text-[#8a6f00]">
              {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"].map((day) => (
                <div key={day} className="py-3">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {calendarCells.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="min-h-[112px] border-b border-r border-slate-100 bg-slate-50" />;
                }

                const date = new Date(month.getFullYear(), month.getMonth(), day);
                const key = eventDateKey(date);
                const dayEntries = entriesByDate.get(key) || [];
                const selected = key === eventDateKey(selectedDate);

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    className={`min-h-[112px] border-b border-r border-slate-100 p-2 text-left transition hover:bg-[#fff8cf] ${
                      selected ? "bg-[#fff3a3]" : "bg-white"
                    }`}
                  >
                    <span
                      className={`inline-grid h-7 w-7 place-items-center rounded-full text-sm font-black ${
                        selected ? "bg-black text-[#ffd400]" : "text-black"
                      }`}
                    >
                      {day}
                    </span>
                    <div className="mt-2 space-y-1">
                      {dayEntries.slice(0, 3).map((entry) => (
                        <div
                          key={`${entry.calendarSource}-${entry._id}`}
                          className={`truncate rounded-full px-2 py-1 text-[10px] font-black ${
                            entry.calendarSource === "cohort"
                              ? "bg-slate-100 text-slate-600"
                              : "bg-[#ffd400] text-black"
                          }`}
                        >
                          {new Date(entry.startsAt).toLocaleTimeString("uk-UA", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          {entry.title}
                        </div>
                      ))}
                      {dayEntries.length > 3 && (
                        <p className="text-[10px] font-black text-slate-500">
                          +{dayEntries.length - 3} ще
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-[26px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
            <div className="border-b border-slate-200 p-5">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                Обраний день
              </p>
              <h2 className="mt-1 text-2xl font-black text-black">
                {selectedDate.toLocaleDateString("uk-UA", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </h2>
            </div>

            <div className="max-h-[650px] space-y-3 overflow-y-auto p-5">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-500">
                  Завантаження...
                </div>
              ) : selectedEntries.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-500">
                  На цей день подій немає.
                </div>
              ) : (
                selectedEntries.map((entry) =>
                  entry.calendarSource === "cohort" ? (
                    <article
                      key={`cohort-${entry._id}`}
                      className="rounded-[22px] border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="mb-2 inline-flex rounded-full bg-white px-3 py-1 text-[10px] font-black uppercase text-slate-600">
                        Подія потоку
                      </div>
                      <h3 className="text-lg font-black text-black">{entry.title}</h3>
                      <p className="mt-2 text-sm font-bold text-slate-600">
                        {entry.cohort?.name || "Потік"} · {formatDateTime(entry.startsAt)}
                      </p>
                    </article>
                  ) : (
                    <article
                      key={entry._id}
                      className="rounded-[22px] border-2 border-black bg-[#fffdf2] p-4 shadow-[4px_4px_0_#ffd400]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className="inline-flex rounded-full bg-[#ffd400] px-3 py-1 text-[10px] font-black uppercase text-black">
                            {kindLabels[entry.kind]}
                          </span>
                          <h3 className="mt-3 text-xl font-black text-black">
                            {entry.title}
                          </h3>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(entry)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-black"
                          >
                            Редагувати
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteEvent(entry._id)}
                            className="grid h-9 w-9 place-items-center rounded-xl border border-red-200 bg-red-50 text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">
                        {entry.description || "Опис поки не додано."}
                      </p>
                      <div className="mt-4 grid gap-2 text-sm font-bold text-slate-700 sm:grid-cols-2">
                        <div className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2">
                          <Clock3 size={16} />
                          {formatDateTime(entry.startsAt)}
                        </div>
                        <div className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2">
                          <MapPin size={16} />
                          {getEventPlaceText(entry)}
                        </div>
                      </div>
                      {entry.kind === "consultation" && (
                        <p className="mt-3 rounded-2xl bg-white px-3 py-2 text-sm font-black text-black">
                          Учасник: {getParticipantName(entry) || "не обрано"}
                        </p>
                      )}
                    </article>
                  )
                )
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-[26px] border border-slate-200 bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                Всі кар'єрні події
              </p>
              <h2 className="text-2xl font-black text-black">Список</h2>
            </div>
            <span className="rounded-full bg-[#fff3a3] px-4 py-2 text-xs font-black text-black">
              {events.length} подій
            </span>
          </div>
          <div className="grid gap-3 lg:grid-cols-3">
            {events.map((event) => (
              <button
                key={event._id}
                type="button"
                onClick={() => openEdit(event)}
                className="rounded-[20px] border border-slate-200 bg-[#fffdf7] p-4 text-left transition hover:border-black hover:bg-[#fff8cf]"
              >
                <span className="rounded-full bg-[#ffd400] px-3 py-1 text-[10px] font-black uppercase text-black">
                  {kindLabels[event.kind]}
                </span>
                <h3 className="mt-3 text-lg font-black text-black">{event.title}</h3>
                <p className="mt-2 text-sm font-bold text-slate-600">
                  {formatDateTime(event.startsAt)}
                </p>
                <p className="mt-2 text-xs font-black uppercase text-[#8a6f00]">
                  {statusLabels[event.status]} · {visibilityLabels[event.visibility]}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 p-3 sm:items-center">
          <form
            onSubmit={saveEvent}
            className="max-h-[92dvh] w-full max-w-[1180px] overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]"
          >
            <div className="flex items-start justify-between gap-4 border-b-2 border-black bg-[#fffdf2] p-5">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                  {editingEventId ? "Редагування" : "Нова подія"}
                </p>
                <h2 className="text-2xl font-black uppercase text-black">
                  {kindLabels[activeKind]}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="grid h-11 w-11 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[calc(92dvh-170px)] overflow-y-auto bg-[#f4f7fb] p-4 sm:p-5">
              <div
                className={
                  activeKind === "consultation"
                    ? "mx-auto max-w-3xl"
                    : "grid gap-5 xl:grid-cols-[minmax(0,1fr)_390px]"
                }
              >
                <div className="space-y-5">
                  <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
                    <div className="mb-4 flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                          Основна інформація
                        </p>
                        <h3 className="mt-1 text-xl font-black text-black">
                          Те, що людина побачить на картці
                        </h3>
                      </div>
                      {activeKind !== "consultation" && (
                        <span className="rounded-full bg-[#fff3a3] px-3 py-1 text-[11px] font-black text-black">
                          live preview
                        </span>
                      )}
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                <label className="block lg:col-span-2">
                  <span className="text-sm font-black text-black">
                    {activeKind === "quick_work"
                      ? "Назва роботи на картці"
                      : activeKind === "partner_vacancy"
                      ? "Назва вакансії на картці"
                      : "Тема консультації"}
                  </span>
                  <input
                    required
                    value={form.title}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                  />
                </label>

                {activeKind === "partner_vacancy" && (
                  <>
                    <label>
                      <span className="text-sm font-black text-black">
                        Компанія на картці
                      </span>
                      <input
                        value={form.company}
                        onChange={(event) =>
                          setForm({ ...form, company: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                    <label>
                      <span className="text-sm font-black text-black">
                        Зарплата на картці
                      </span>
                      <input
                        value={form.payout}
                        onChange={(event) =>
                          setForm({ ...form, payout: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                  </>
                )}

                {activeKind === "quick_work" && (
                  <>
                    <label>
                      <span className="text-sm font-black text-black">
                        Рядок оплати
                      </span>
                      <input
                        value={form.payout}
                        placeholder="Наприклад: від 800 грн / день"
                        onChange={(event) =>
                          setForm({ ...form, payout: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                    <label>
                      <span className="text-sm font-black text-black">
                        Рядок “коли старт”
                      </span>
                      <input
                        value={form.startLabel}
                        placeholder="Наприклад: можна вийти завтра"
                        onChange={(event) =>
                          setForm({ ...form, startLabel: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                    <label>
                      <span className="text-sm font-black text-black">
                        Рядок “формат / місто”
                      </span>
                      <input
                        value={form.formatLabel}
                        placeholder="Наприклад: офлайн, Харків"
                        onChange={(event) =>
                          setForm({ ...form, formatLabel: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                    <label>
                      <span className="text-sm font-black text-black">
                        Теги на картці
                      </span>
                      <input
                        value={(form.tags || []).join(", ")}
                        placeholder="Наприклад: разові зміни, швидка виплата, без досвіду"
                        onChange={(event) =>
                          setForm({
                            ...form,
                            tags: event.target.value
                              .split(",")
                              .map((tag) => tag.trim())
                              .filter(Boolean),
                          })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                  </>
                )}

                {activeKind === "consultation" && (
                  <label className="lg:col-span-2">
                    <span className="text-sm font-black text-black">Учасник</span>
                    <select
                      required
                      value={form.primaryParticipantId}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          primaryParticipantId: event.target.value,
                        })
                      }
                      className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                    >
                      <option value="">Оберіть учасника</option>
                      {users.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.username || user.telephone || user.email} ·{" "}
                          {user.telephone || user.email || "контакт не вказано"}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label>
                  <span className="text-sm font-black text-black">
                    Дата і час у календарі
                  </span>
                  <input
                    required
                    type="datetime-local"
                    step={DATETIME_STEP_SECONDS}
                    value={form.startsAt}
                    onChange={(event) =>
                      setForm({ ...form, startsAt: event.target.value })
                    }
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                  />
                </label>
                <label>
                  <span className="text-sm font-black text-black">
                    Завершення в календарі
                  </span>
                  <input
                    required
                    type="datetime-local"
                    step={DATETIME_STEP_SECONDS}
                    value={form.endsAt}
                    onChange={(event) =>
                      setForm({ ...form, endsAt: event.target.value })
                    }
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                  />
                </label>

                <label>
                  <span className="text-sm font-black text-black">Статус</span>
                  <select
                    value={form.status}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        status: event.target.value as CareerEventStatus,
                      })
                    }
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="text-sm font-black text-black">Публікація</span>
                  <select
                    value={form.visibility}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        visibility: event.target.value as CareerEventVisibility,
                      })
                    }
                    className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                  >
                    {Object.entries(visibilityLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                {activeKind !== "quick_work" && (
                  <>
                    <label>
                      <span className="text-sm font-black text-black">
                        {activeKind === "partner_vacancy"
                          ? "Локація на картці"
                          : "Локація зустрічі"}
                      </span>
                      <input
                        value={form.location}
                        placeholder={
                          activeKind === "partner_vacancy"
                            ? "Наприклад: віддалено / Харків"
                            : "Наприклад: Yellow Hub, вул. Громадянська 17"
                        }
                        onChange={(event) =>
                          setForm({ ...form, location: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                    <label>
                      <span className="text-sm font-black text-black">
                        {activeKind === "partner_vacancy"
                          ? "Графік на картці"
                          : "Коротка примітка"}
                      </span>
                      <input
                        value={form.schedule}
                        placeholder={
                          activeKind === "partner_vacancy"
                            ? "Наприклад: 5/2 або зміни"
                            : "Наприклад: індивідуальна зустріч"
                        }
                        onChange={(event) =>
                          setForm({ ...form, schedule: event.target.value })
                        }
                        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                  </>
                )}

                {activeKind === "partner_vacancy" && (
                  <label className="lg:col-span-2">
                    <span className="text-sm font-black text-black">
                      Контакти роботодавця на картці
                    </span>
                    <input
                      value={form.employerContact}
                      placeholder="Наприклад: +380... або email роботодавця"
                      onChange={(event) =>
                        setForm({ ...form, employerContact: event.target.value })
                      }
                      className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-[#ffd400]"
                    />
                  </label>
                )}

                {activeKind !== "consultation" && (
                  <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-[#fffdf4] p-4">
                    <p className="text-sm font-black text-black">
                      Категорія на сайті
                    </p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                      Якщо нічого не обрати, оголошення буде звичайною вакансією.
                      Можна відмітити один додатковий формат.
                    </p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <input
                          type="checkbox"
                          checked={Boolean(form.isInstantWork)}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              isInstantWork: event.target.checked,
                              isDigitalWork: event.target.checked
                                ? false
                                : form.isDigitalWork,
                            })
                          }
                          className="mt-1 h-5 w-5 rounded border-slate-300 accent-[#ffd400]"
                        />
                        <span>
                          <span className="block text-sm font-black text-black">
                            Миттєва робота
                          </span>
                          <span className="mt-1 block text-xs font-semibold leading-5 text-slate-500">
                            Разова робота або швидкий старт.
                          </span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <input
                          type="checkbox"
                          checked={Boolean(form.isDigitalWork)}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              isDigitalWork: event.target.checked,
                              isInstantWork: event.target.checked
                                ? false
                                : form.isInstantWork,
                            })
                          }
                          className="mt-1 h-5 w-5 rounded border-slate-300 accent-[#ffd400]"
                        />
                        <span>
                          <span className="block text-sm font-black text-black">
                            Цифрова робота
                          </span>
                          <span className="mt-1 block text-xs font-semibold leading-5 text-slate-500">
                            Онлайн, Canva, документи.
                          </span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <input
                          type="checkbox"
                          checked={Boolean(form.hasReservation)}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              hasReservation: event.target.checked,
                            })
                          }
                          className="mt-1 h-5 w-5 rounded border-slate-300 accent-[#ffd400]"
                        />
                        <span>
                          <span className="block text-sm font-black text-black">
                            Є бронювання
                          </span>
                          <span className="mt-1 block text-xs font-semibold leading-5 text-slate-500">
                            Окрема плашка на картці.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                <label className="lg:col-span-2">
                  <span className="text-sm font-black text-black">
                    {activeKind === "quick_work"
                      ? "Опис задачі на картці"
                      : activeKind === "partner_vacancy"
                      ? "Опис вакансії на картці"
                      : "Тема / опис консультації"}
                  </span>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(event) =>
                      setForm({ ...form, description: event.target.value })
                    }
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none focus:border-[#ffd400]"
                  />
                </label>

                {activeKind !== "consultation" && (
                  <>
                    <label className="lg:col-span-2">
                      <span className="text-sm font-black text-black">
                        Що потрібно робити
                      </span>
                      <p className="mt-1 text-xs font-semibold text-slate-500">
                        Кожен пункт пиши з нового рядка. На сайті це стане
                        акуратним списком.
                      </p>
                      <textarea
                        rows={5}
                        value={form.responsibilities || ""}
                        placeholder={
                          "сухе та вологе прибирання кабінетів\nприбирання холу та санвузлів\nпідтримання чистоти протягом зміни"
                        }
                        onChange={(event) =>
                          setForm({
                            ...form,
                            responsibilities: event.target.value,
                          })
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>

                    <label className="lg:col-span-2">
                      <span className="text-sm font-black text-black">
                        Що пропонуємо
                      </span>
                      <p className="mt-1 text-xs font-semibold text-slate-500">
                        Умови, бонуси, навчання, бронювання, адаптація.
                      </p>
                      <textarea
                        rows={5}
                        value={form.benefits || ""}
                        placeholder={
                          "13 000 грн + бонуси\nграфік 5/2\nнавчання та стажування\nпідтримка під час адаптації"
                        }
                        onChange={(event) =>
                          setForm({ ...form, benefits: event.target.value })
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>

                    <label className="lg:col-span-2">
                      <span className="text-sm font-black text-black">
                        Вимоги
                      </span>
                      <p className="mt-1 text-xs font-semibold text-slate-500">
                        Можна залишити порожнім, якщо досвід не потрібен.
                      </p>
                      <textarea
                        rows={4}
                        value={form.requirements || ""}
                        placeholder={
                          "досвід не потрібен\nвідповідальність та уважність\nготовність працювати за графіком"
                        }
                        onChange={(event) =>
                          setForm({
                            ...form,
                            requirements: event.target.value,
                          })
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none focus:border-[#ffd400]"
                      />
                    </label>
                  </>
                )}

                    </div>
                  </div>
                </div>

                {activeKind !== "consultation" && (
                  <aside className="xl:sticky xl:top-0 xl:self-start">
                    <div className="rounded-[26px] border-2 border-black bg-white shadow-[6px_6px_0_#ffd400]">
                      <div className="h-2 rounded-t-[24px] bg-[#ffd400]" />
                      <div className="p-5">
                        <div className="mb-5 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#fff1a8] px-3 py-1.5 text-[11px] font-black text-[#665000]">
                            {getFormJobCategory(form)}
                          </span>
                          <span className="max-w-[150px] truncate rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-500">
                            {form.company || "Назва компанії"}
                          </span>
                          {form.hasReservation && (
                            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[11px] font-black text-emerald-700">
                              Бронювання
                            </span>
                          )}
                        </div>

                        <h3 className="text-[24px] font-black leading-[1.05] text-black">
                          {form.title || "Назва пропозиції"}
                        </h3>
                        <p className="mt-3 line-clamp-4 text-sm font-semibold leading-6 text-slate-600">
                          {form.description ||
                            "Коротко опиши суть роботи: кого шукаємо, що робити і чому варто відгукнутись."}
                        </p>

                        <div className="mt-5 rounded-[20px] bg-[#faf7ed] p-4">
                          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                            Оплата
                          </p>
                          <p className="mt-1 text-[18px] font-black text-black">
                            {form.payout || "умови уточнюються"}
                          </p>
                          <div className="mt-4 space-y-2 text-[13px] font-bold text-slate-700">
                            <div className="flex items-start gap-2">
                              <Clock3
                                size={15}
                                className="mt-0.5 shrink-0 text-[#8a6f00]"
                              />
                              <span>
                                {form.schedule ||
                                  form.startLabel ||
                                  "графік уточнюється"}
                              </span>
                            </div>
                            <div className="flex items-start gap-2">
                              <MapPin
                                size={15}
                                className="mt-0.5 shrink-0 text-[#8a6f00]"
                              />
                              <span>
                                {form.location ||
                                  form.formatLabel ||
                                  "локація уточнюється"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {(splitFormList(form.responsibilities).length > 0 ||
                          splitFormList(form.benefits).length > 0 ||
                          splitFormList(form.requirements).length > 0) && (
                          <div className="mt-5 space-y-3 rounded-[20px] border border-slate-200 p-4">
                            {[
                              {
                                title: "Що робити",
                                items: splitFormList(form.responsibilities),
                              },
                              {
                                title: "Що пропонуємо",
                                items: splitFormList(form.benefits),
                              },
                              {
                                title: "Вимоги",
                                items: splitFormList(form.requirements),
                              },
                            ]
                              .filter((section) => section.items.length > 0)
                              .map((section) => (
                                <div key={section.title}>
                                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                                    {section.title}
                                  </p>
                                  <ul className="mt-1 space-y-1 text-xs font-semibold leading-5 text-slate-600">
                                    {section.items.slice(0, 3).map((item) => (
                                      <li key={item}>• {item}</li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                          </div>
                        )}

                        <div className="mt-5 rounded-[18px] border border-dashed border-[#d8bd28] bg-[#fffdf2] p-4">
                          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                            Контакти роботодавця
                          </p>
                          <p className="mt-2 truncate rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-500 blur-[2px]">
                            {form.employerContact || "після реєстрації"}
                          </p>
                        </div>

                        <div className="mt-5 rounded-full bg-black px-5 py-3 text-center text-sm font-black text-[#ffd400]">
                          Відгукнутись →
                        </div>
                      </div>
                    </div>
                  </aside>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 p-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-black"
              >
                Скасувати
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 py-3 text-sm font-black text-black shadow-[3px_3px_0_#000] disabled:opacity-60"
              >
                <Save size={17} />
                {saving ? "Зберігаю..." : "Зберегти"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default AdminCareerPage;
